import React from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Delete, Email, Refresh } from "@mui/icons-material";
import ExpandableRow from "../ExpandableRow";
import type { RFQ } from "../../../../types/rfq";
import type { RFQTab } from "../TabNavigation";
import type { RfqSelection } from "./useRfqSelection";

interface RfqTableProps {
  rfqs: RFQ[];
  activeTab: RFQTab;
  selection: RfqSelection;
  expandedRfqId: number | null;
  isRefreshing: boolean;
  onRefresh: () => void;
  onCheckEmails: () => void;
  onMassDelete: () => void;
  onDelete: (rfqId: number) => void;
  onViewAttachments: (rfqId: number) => void;
  onToggleExpanded: (rfqId: number) => void;
  onBlacklist: (rfqId: number, clientEmail: string, clientName: string) => void;
}

/** "No RFQs found" with refresh and email-check buttons, as the table's only row. */
function EmptyRow({ activeTab, isRefreshing, onRefresh, onCheckEmails }: Pick<RfqTableProps, "activeTab" | "isRefreshing" | "onRefresh" | "onCheckEmails">) {
  return (
    <TableRow>
      <TableCell colSpan={6} align="center">
        <Box
          sx={{
            py: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ color: "text.secondary", fontWeight: 500 }}>
            No RFQs found with status "{activeTab}"
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
            <Button
              variant="contained"
              onClick={onRefresh}
              startIcon={<Refresh />}
              sx={{
                px: 3,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Refresh Data
            </Button>
            <Button
              variant="outlined"
              onClick={onCheckEmails}
              startIcon={<Email />}
              sx={{
                px: 3,
                textTransform: "none",
                fontWeight: 500,
              }}
              disabled={isRefreshing}
            >
              Check for New Emails
              {isRefreshing && <CircularProgress size={16} sx={{ ml: 1 }} />}
            </Button>
          </Box>
        </Box>
      </TableCell>
    </TableRow>
  );
}

/** The RFQ table: mass-delete bar, select-all header, one expandable row per RFQ, total. */
export default function RfqTable({
  rfqs,
  activeTab,
  selection,
  expandedRfqId,
  isRefreshing,
  onRefresh,
  onCheckEmails,
  onMassDelete,
  onDelete,
  onViewAttachments,
  onToggleExpanded,
  onBlacklist,
}: RfqTableProps) {
  const theme = useTheme();
  const { selectedRfqs } = selection;

  return (
    <Paper sx={{ borderRadius: 1, overflow: "hidden" }}>
      {/* Mass Delete Action Bar */}
      {selectedRfqs.length > 0 && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1.25,
          bgcolor: alpha(theme.palette.error.main, 0.05),
          borderBottom: 1,
          borderColor: 'divider',
        }}>
          <Typography variant="subtitle2">
            {selectedRfqs.length} RFQ{selectedRfqs.length > 1 ? 's' : ''} selected
          </Typography>
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<Delete />}
            onClick={onMassDelete}
          >
            Delete Selected
          </Button>
        </Box>
      )}

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 52 }}>
                <Checkbox
                  indeterminate={selectedRfqs.length > 0 && selectedRfqs.length < rfqs.length}
                  checked={selection.selectAllChecked}
                  onChange={selection.handleSelectAll}
                  sx={{ ml: 0.5 }}
                />
              </TableCell>
              <TableCell sx={{ width: { xs: 104, md: 120 } }}>RFQ ID</TableCell>
              <TableCell>Client</TableCell>
              <TableCell sx={{ width: { xs: 124, md: 140 } }}>Received</TableCell>
              {/* Every row in a tab shares one status, so narrow screens drop the column */}
              <TableCell sx={{ width: 110, display: { xs: "none", md: "table-cell" } }}>Status</TableCell>
              {/* Shrinks to its buttons so Client takes the spare width */}
              <TableCell sx={{ width: "1%" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rfqs.length === 0 ? (
              <EmptyRow activeTab={activeTab} isRefreshing={isRefreshing} onRefresh={onRefresh} onCheckEmails={onCheckEmails} />
            ) : (
              rfqs.map((rfq) => (
                  <React.Fragment key={rfq.rfq_id}>
                    <ExpandableRow
                      rfq={rfq}
                      onDelete={() => onDelete(rfq.rfq_id)}
                      onSelect={selection.toggleRfq}
                      isSelected={selectedRfqs.includes(rfq.rfq_id)}
                      onViewAttachments={onViewAttachments}
                      expanded={expandedRfqId === rfq.rfq_id}
                      onToggleExpanded={onToggleExpanded}
                      onBlacklist={onBlacklist}
                    />
                  </React.Fragment>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Show total count at bottom */}
      {rfqs.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {rfqs.length} RFQ{rfqs.length !== 1 ? 's' : ''} (latest first)
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
